import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { withAuth } from "../../../components/HOC/withAuth";
import { createCompanyUser } from "../../../services/users";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  FiUsers,
  FiMail,
  FiPhone,
  FiMapPin,
  FiSettings,
  FiBell,
  FiSave,
  FiLock,
} from "react-icons/fi";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import Lozenge from "../../../components/BaseUI/Lozenge";
import Label from "../../../components/BaseUI/Label";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const CreateCompany = () => {
  const navigate = useNavigate();
  const { getToken, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const canWrite =
    !!user &&
    hasPageWriteAccess(
      user.role,
      "/companies",
      user.pages || [],
      !!user.fullAccess,
    );
  const [companyData, setCompanyData] = useState({
    name: "",
    code: "",
    email: "",
    phone: "",
    address: "",
    status: "active",
    taxNumber: "",
    taxOffice: "",
    yetkiliKisi: "",
  });

  const breadcrumbs = [
    { label: "Rovion", url: "" },

    { label: translate("UI.COMPANIES"), url: "/companies" },
    { label: translate("UI.ADD") },
  ];

  const handleInputChange = (field, value) => {
    // Auto uppercase for company code
    if (field === "code") {
      value = value.toUpperCase();
    }

    if (field.includes(".")) {
      const [section, subField] = field.split(".");
      setCompanyData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [subField]: value,
        },
      }));
    } else {
      setCompanyData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const validateForm = () => {
    const errors = [];

    // Check required fields
    const requiredFields = ["name", "code", "email", "phone", "address"];
    const missingFields = requiredFields.filter((field) => !companyData[field]);

    if (missingFields.length > 0) {
      errors.push(
        `${translate("UI.PLEASE_FILL_ALL_REQUIRED_FIELDS")}: ${missingFields
          .map((field) => translate(`UI.${field.toUpperCase()}`))
          .join(", ")}`,
      );
    }

    // Check email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (companyData.email && !emailRegex.test(companyData.email)) {
      errors.push(translate("UI.INVALID_EMAIL_FORMAT"));
    }

    // Check company code minimum length
    if (companyData.code && companyData.code.length < 3) {
      errors.push(translate("UI.COMPANY_CODE_MIN_LENGTH"));
    }

    return errors;
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Form validation
      const errors = validateForm();
      if (errors.length > 0) {
        setToasts([
          {
            description: errors.join("\n"),
            id: Date.now(),
            title: translate("UI.ERROR"),
            appearance: "danger",
          },
        ]);
        return;
      }

      const response = await axios.post(
        `${API_URL}/api/companies`,
        companyData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        setToasts([
          {
            description: translate("UI.COMPANY_CREATED_SUCCESSFULLY"),
            id: Date.now(),
            title: translate("UI.SUCCESS"),
            appearance: "success",
          },
        ]);

        setTimeout(() => {
          navigate("/companies");
        }, 1500);
      }
    } catch (error) {
      console.error("Error creating company:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            translate("UI.ERROR_CREATING_COMPANY"),
          id: Date.now(),
          title: translate("UI.ERROR"),
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  if (!canWrite) {
    return (
      <AppPage title={translate("UI.ADD_COMPANY")} breadcrumbs={breadcrumbs}>
        <Container>
          <ContainerItem>
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <span>Bu sayfada değişiklik yapma yetkiniz yok.</span>
              <Button
                appearance="primary-animated"
                onClick={() => navigate("/companies")}
              >
                Geri Dön
              </Button>
            </div>
          </ContainerItem>
        </Container>
      </AppPage>
    );
  }

  return (
    <AppPage title={translate("UI.ADD_COMPANY")} breadcrumbs={breadcrumbs}>
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="card shadow-sm">
            <div className="card-body">
              {/* Company Information */}
              <div className="section-container">
                <div className="section-header">
                  <div className="section-icon">
                    <FiUsers className="text-primary" />
                  </div>
                  <div className="section-title">
                    <h5 className="mb-1">
                      {translate("UI.COMPANY_INFORMATION")}
                    </h5>
                    <p className="text-muted small mb-0">
                      {translate("UI.COMPANY_INFO_DESC")}
                    </p>
                  </div>
                </div>

                <div className="section-content">
                  <div className="d-flex flex-wrap gap-3">
                    <div className="w-45">
                      <Input
                        type="text"
                        label={translate("UI.NAME")}
                        value={companyData.name}
                        onChange={(value) => handleInputChange("name", value)}
                        required
                        prefix={<FiUsers className="text-muted" />}
                        hint={translate("UI.COMPANY_NAME_DESC")}
                      />
                    </div>
                    <div className="w-45">
                      <Input
                        type="text"
                        label={translate("UI.COMPANY_CODE")}
                        value={companyData.code}
                        onChange={(value) => handleInputChange("code", value)}
                        required
                        hint={translate("UI.COMPANY_CODE_DESC")}
                      />
                    </div>
                    <div className="w-45">
                      <Input
                        type="email"
                        label="Email"
                        value={companyData.email}
                        onChange={(value) => handleInputChange("email", value)}
                        required
                        prefix={<FiMail className="text-muted" />}
                      />
                    </div>
                    <div className="w-45">
                      <Input
                        type="tel"
                        label={translate("UI.PHONE")}
                        value={companyData.phone}
                        onChange={(value) => handleInputChange("phone", value)}
                        required
                        prefix={<FiPhone className="text-muted" />}
                      />
                    </div>
                    <div className="w-45">
                      <Input
                        type="text"
                        label={translate("UI.ADDRESS")}
                        value={companyData.address}
                        onChange={(value) =>
                          handleInputChange("address", value)
                        }
                        required
                        prefix={<FiMapPin className="text-muted" />}
                      />
                    </div>
                    <div className="w-45">
                      <Input
                        type="text"
                        label={translate("UI.TAX_NUMBER")}
                        value={companyData.taxNumber}
                        onChange={(value) =>
                          handleInputChange("taxNumber", value)
                        }
                        prefix={<FiMapPin className="text-muted" />}
                        hint={translate("UI.TAX_NUMBER_DESC")}
                      />
                    </div>
                    <div className="w-45">
                      <Input
                        type="text"
                        label={translate("UI.TAX_OFFICE")}
                        value={companyData.taxOffice}
                        onChange={(value) =>
                          handleInputChange("taxOffice", value)
                        }
                        prefix={<FiMapPin className="text-muted" />}
                        hint={translate("UI.TAX_OFFICE_DESC")}
                      />
                    </div>
                    <div className="w-45">
                      <Input
                        type="text"
                        label="Yetkili Kişi"
                        value={companyData.yetkiliKisi}
                        onChange={(value) =>
                          handleInputChange("yetkiliKisi", value)
                        }
                        prefix={<FiUsers className="text-muted" />}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="form-actions p-3">
                <Button
                  appearance="danger-subtle"
                  onClick={() => navigate("/companies")}
                >
                  {translate("UI.CANCEL")}
                </Button>
                <Button
                  appearance="primary-animated"
                  onClick={handleSubmit}
                  loading={loading}
                  icon={<FiSave />}
                >
                  {translate("UI.SAVE")}
                </Button>
              </div>
            </div>
          </div>
        </ContainerItem>
      </Container>

      <style>
        {`
                .section-container {
                    margin-bottom: 2rem;
                    padding: 1.5rem;
                    background: #fff;
                    border-radius: 8px;
                    border: 1px solid #e9ecef;
                }

                .section-container:last-child {
                    margin-bottom: 0;
                }

                .section-header {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }

                .section-icon {
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--bs-primary-bg-subtle);
                    border-radius: 8px;
                }

                .section-icon svg {
                    width: 24px;
                    height: 24px;
                }

                .section-title h5 {
                    font-size: 1.1rem;
                    margin: 0;
                    color: var(--bs-gray-900);
                }

                .section-content {
                    padding: 0 0.5rem;
                }

                .form-control-container {
                    background: #fff;
                    border: 1px solid #e2e5e8;
                    border-radius: 6px;
                    transition: all 0.2s ease;
                }

                .form-control-container:hover {
                    border-color: var(--bs-primary);
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 1rem;
                    margin-top: 2rem;
                    padding-top: 1rem;
                    border-top: 1px solid #e9ecef;
                }

                @media (max-width: 768px) {
                    .section-container {
                        padding: 1rem;
                    }

                    .section-header {
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                        gap: 0.5rem;
                    }

                    .section-icon {
                        width: 32px;
                        height: 32px;
                    }

                    .section-icon svg {
                        width: 20px;
                        height: 20px;
                    }
                }
                `}
      </style>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            // actions={[{ content: "Kapat", onClick: () => removeToast(index) }]}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(CreateCompany, ["admin"]);
